<!-- This is the base window search component. Other window search components will be built off this one. -->
<script lang="ts" setup generic="T extends Record<string, any>">
import { reactive, watch, computed, ref, nextTick } from 'vue';
import vSelect from 'vue-select'; 
import MessageBadge from "../../components/Messages/MessageBadge.vue"
import { DisplayStyle } from '../../Enums/UiEnums'; 
import { WindowSearchColumnNameItem, WindowSearchAdditionalPlacement, WindowSearchDropdownAlignment } from '../../lib/Api/WindowSearch';
import { debounce } from 'lodash';
import '../../../css/choice.css';

const vselectRef = ref<InstanceType<typeof vSelect> | null>(null);
const emit = defineEmits(['update:modelValue','onSearch','onNew','onReset']);

defineExpose({
    focus: () => {
        // Try to focus the input element inside v-select
        const el = vselectRef.value?.$el as HTMLElement | undefined;
        if (el) {
            const input = el.querySelector('input');
            if (input) (input as HTMLInputElement).focus();
        }
    }
});

const props = withDefaults(defineProps<{
    modelValue: T | null,  // This is the selected option (json object)
    columns: WindowSearchColumnNameItem[],  // This is the columns to display in the search results
    selectedColumn: string, // This is the column to display when an option is selected
    searchResults: T[], // These are the search results which are set by the parent
    totalRows: number, // This is the total number of rows that could have been returned by the search, but the actual results were limited by the maximum number of rows
    maxReturnRows: number,  // This is the maximum number of rows returned by the search
    newLink?: boolean, // Display the new button
    newLinkTooltip?: string, // Display the new button tooltip,
    disabled?: boolean, // Disable the control
    placeholder?: string, // Display a placeholder
    label?: string // label name to display top of the control
    additionalPlacement?: WindowSearchAdditionalPlacement,
    fixedSearchWidth?: number, // If set, this will fix the width of the search input to this value
    dropdownWidth?: number, // If set, this will fix the width of the dropdown to this value
    dropdownPlacement?: WindowSearchDropdownAlignment, // This will set the alignment of the dropdown
    dropdownHeight?: number // If set, this will fix the height of the dropdown to this value
}>(), {
    newLink: false,
    newLinkTooltip: "",
    disabled: false,
    additionalPlacement: WindowSearchAdditionalPlacement.Top,
    dropdownPlacement: WindowSearchDropdownAlignment.Left
});

const formData = reactive<{
    selectedOption: any | null,
    searchResults: any[],
    totalRows: number,
    maxReturnRows: number,
    loading: boolean,
    messageBadgeTooltip: string,
    disabled: boolean,
    dropdownWidth?: number
}>({
    selectedOption: props.modelValue,
    searchResults: [],
    totalRows: props.totalRows,
    maxReturnRows: props.maxReturnRows,
    loading: false,
    messageBadgeTooltip: "",
    disabled: false
});

watch(() => props.modelValue, (_new, _old) => {
    if (_new !== _old) {
        formData.selectedOption = _new;
        if (_new) {
            formData.searchResults = [_new];
        } else {
            formData.searchResults = [];
        }
    }   
});

watch(() => props.searchResults, (_new, _old) => {
    if (_new !== _old) {
        formData.searchResults = _new;
        formData.loading = false;
    }   
});

watch(() => props.totalRows, (_new, _old) => {
    if (_new !== _old) {
        formData.totalRows = _new;
    }   
});

watch(() => props.maxReturnRows, (_new, _old) => {
    if (_new !== _old) {
        formData.maxReturnRows = _new;
    }   
});

watch(() => props.disabled, (_new, _old) => {
    if (_new !== _old) {
        formData.disabled = _new;
    }   
});

const computedSearchClass = computed(() => formData.loading ?  "fa-solid fa-spinner fa-spin mt-1" : "fa-solid fa-magnifying-glass mt-1");
const computedSearchStyle = computed(() => props.fixedSearchWidth != null ?  `width: ${props.fixedSearchWidth}px; font-size: 0.75rem` : "font-size: 0.75rem");
const computedDropdownStyle = computed(() => {
    if (props.dropdownWidth == null && props.dropdownHeight == null)
        return "";
    let styleHeight = "";
    if (props.dropdownHeight != null) {
        styleHeight = `--dropdown-height: ${props.dropdownHeight}px;`;
    } else {
        styleHeight = `--dropdown-height: 300px;`; // Default height if not set
    }
    if (props.dropdownWidth != null) {
        if (props.dropdownPlacement === WindowSearchDropdownAlignment.Left) {
            return `--dropdown-min-width: ${props.dropdownWidth}px; --dropdown-max-width: ${props.dropdownWidth}px; --dropdown-left: 0; --dropdown-right: auto; ${styleHeight}`;
        } else if (props.dropdownPlacement === WindowSearchDropdownAlignment.Right) {
            return `--dropdown-min-width: ${props.dropdownWidth}px; --dropdown-max-width: ${props.dropdownWidth}px; --dropdown-left: auto !important; --dropdown-right: 0; ${styleHeight}`;
        } else {
            return `--dropdown-min-width: ${props.dropdownWidth}px; --dropdown-max-width: ${props.dropdownWidth}px; ${styleHeight}`;
        }
    }
    return styleHeight;   
});
const messageBadgeText = computed(() => {
    if (formData.totalRows === 0) {
        return "";
    } else if (formData.searchResults.length === 0) {
        return "";
    } else if (formData.totalRows > formData.maxReturnRows) {
        return `${formData.maxReturnRows}/${formData.totalRows}`;
    } 
    return "";
});
const messageBadgeTooltip = computed(() => {
    if (formData.totalRows === 0) {
        return "";
    } else if (formData.searchResults.length === 0) {
        return "";
    } else if (formData.totalRows > formData.maxReturnRows) {
        return `Displaying the top ${formData.maxReturnRows} out of a possible ${formData.totalRows} rows. Please refine your search.`;
    } 
    return "";
});
const displayLabelRow = computed(() => {
    if (props.label != null && props.label.length > 0) {
        return true;
    } 
    if (messageBadgeText.value.length > 0) {
        return true;
    }
    if (props.newLink) {
        return true;
    }
    return false;
});


// You must provide a search function from the parent for true generic use
const performSearch = async (searchText: string) => {
    resetSearchResults();
    if (searchText.length < 3) {
        return; // Search text too short, skip the search
    }
    formData.loading = true;
    await nextTick();
    emit('onSearch', searchText);
};

function onOptionSelected(option: T) {
    formData.totalRows = option == null ? 0 : 1; // This will clear the message badge
    emit('update:modelValue', option);
}

function onUpdateModel(option: T) {
    if (option == null) {
        // Only do this for null otherwise modelValue will get sent twice
        emit('update:modelValue', option);
    }
}

async function onNew() {
    if (formData.disabled)
        return
    emit('onNew');
}

function onCloseHandler() {
    initializeSearch();
}

function initializeSearch() {
    if (props.modelValue) {
        formData.searchResults = [props.modelValue];
        formData.selectedOption = props.modelValue;
        formData.totalRows = 1;
        formData.maxReturnRows = 1;
    } else {
        formData.selectedOption = null;
        resetSearchResults();
    }
}

function resetSearchResults() {
    formData.searchResults = [];
    formData.totalRows = 0;
    emit('onReset');
}

const debouncedSearch = debounce(performSearch, 400);
</script>

<style scoped>
    .option-container {
        display: grid;
    }
    .column {
        padding: 2px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
    :deep(.vs__dropdown-toggle) {
        border-top-left-radius: 0; /* Remove rounded corners if desired */
        border-bottom-left-radius: 0; /* Remove rounded corners if desired */
        border-left: none;
        font-size: 0.75rem;
    }
    :deep(.vs__dropdown-option) {
        padding-left: 4px;
        padding-right: 4px;
    }
    :deep(.vs__dropdown-menu) {
        min-width: var(--dropdown-min-width);
        max-width: var(--dropdown-max-width);
        left: var(--dropdown-left);
        right: var(--dropdown-right);
        max-height: var(--dropdown-height, 300px); /* Default height if not set */
    }
    .input-group-text {
        border-top-right-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
    }
</style>

<template>
    <div v-if="displayLabelRow" class="row">
        <div class="col label-isalus-small">
            {{ props.label }}
        </div>
        <template v-if="props.additionalPlacement == WindowSearchAdditionalPlacement.Top">
            <div class="col-auto">
                <MessageBadge :displayStyle="DisplayStyle.Danger" :title="messageBadgeTooltip">
                    {{ messageBadgeText }}
                </MessageBadge>
            </div>
            <div v-if="props.newLink" class="col-auto">
                <span @click="onNew" :title="props.newLinkTooltip" class="isalus-link" style="font-size: 0.75rem">
                    New
                </span>
            </div>
        </template>
    </div>
    <div class="row">
        <template v-if="props.additionalPlacement == WindowSearchAdditionalPlacement.Left">
            <div v-if="props.newLink" class="col-auto mt-1 pe-0">
                <span @click="onNew" :title="props.newLinkTooltip" class="isalus-link" style="font-size: 0.75rem">
                    New
                </span>
            </div>
            <div class="col-auto mt-1 pe-0">
                <MessageBadge :displayStyle="DisplayStyle.Danger" :title="messageBadgeTooltip">
                    {{ messageBadgeText }}
                </MessageBadge>
            </div>
        </template>
        <div class="col-auto pe-0">
            <span class="input-group-text">
                <em :class="computedSearchClass"></em>
            </span>
        </div>
        <div class="col ps-0" :style="computedDropdownStyle">
            <v-select 
                ref="vselectRef"
                :options="formData.searchResults"
                v-model="formData.selectedOption"
                @search="debouncedSearch"
                @option:selected="onOptionSelected"
                @update:model-value="onUpdateModel"
                @close="onCloseHandler"
                :style="computedSearchStyle"
                :filterable="false"
                :disabled="formData.disabled"
                :placeholder="props.placeholder">
                <template #option="option">
                    <div class="option-container" :style="{gridTemplateColumns: props.columns.map(col => col.width || '1fr').join(' ')}">
                        <div class="column" v-for="columnItem in props.columns" :title="`${columnItem.tooltip}: ${option[columnItem.columnName]}`" :key="columnItem.columnName">
                            {{ option[columnItem.columnName] }}
                        </div>
                    </div>
                </template>
                <template #selected-option="selectedOption">
                    <div class="option-container">
                        <div class="column">{{ selectedOption[props.selectedColumn] }}</div>
                    </div>
                </template>
                <template #no-options>
                    <div style="padding: 8px; color: #888;">
                    No results found. Try a different search.
                    </div>
                </template>
            </v-select>
        </div>
        <template v-if="props.additionalPlacement == WindowSearchAdditionalPlacement.Right">
            <div v-if="messageBadgeText.length > 0" class="col-auto mt-1 ps-1">
                <MessageBadge :displayStyle="DisplayStyle.Danger" :title="messageBadgeTooltip">
                    {{ messageBadgeText }}
                </MessageBadge>
            </div>
            <div v-if="props.newLink" class="col-auto mt-1 ps-1">
                <span @click="onNew" :title="props.newLinkTooltip" class="isalus-link" style="font-size: 0.75rem">
                    New
                </span>
            </div>
        </template>
    </div>
</template>
